import { Col, Divider, Modal, Row, Typography } from "antd"
import { observer } from "mobx-react"
import { useTranslation } from "react-i18next"
import MainForm, { MainFormRef } from "../../../components/Form/MainForm"
import { useContainerInjection } from "../../../hooks/useContainerInjection"
import { createRef, useEffect, useState } from "react"
import { FormField } from "../../../components/Form/FormField"
import { SellerServicesViewModel } from "../../sellersServices/SellerServicesViewModel"
import { InputType } from "../../../components/TextInput/const/InputType.enum"
import { MicrositesViewModel } from "../MicrositesViewModel"
import { LandingReview } from "../../../../domain/entities/LandingReview"

export interface MicrositeLandingReviewModalProps {
	onConfirm?: (review: { tipId: number; position: number }) => void
	onCancel?: () => void
	visible: boolean
	editting?: boolean
}

const MicrositeTipModal = ({ visible, onConfirm, onCancel }: MicrositeLandingReviewModalProps) => {
	const { t } = useTranslation("microsites", { keyPrefix: "form.tipsInfo" })
	const micrositeViewModel = useContainerInjection<MicrositesViewModel>("MicrositesViewModel")
	const initialModalData = {
		id: undefined,
		position: 1
	}
	const [modalData, setModalData] = useState<{ id?: number; position: number }>(initialModalData)
	const formRef = createRef<MainFormRef>()

	useEffect(() => {
		setModalData(initialModalData)
		micrositeViewModel.clearSearchedTips()
	}, [visible])

	return (
		<Modal
			title={
				<div style={{ position: "relative", bottom: 24 }}>
					<Typography.Title level={5}>{t("modalTitle")}</Typography.Title>
					<Divider style={{ marginBottom: 0 }} />
				</div>
			}
			centered
			style={{ overflowY: "auto" }}
			open={visible}
			onOk={async () => {
				await formRef.current?.validateFields()
				const tip = micrositeViewModel.tips.find(t => t.id === modalData.id)
				tip && onConfirm && onConfirm({ tipId: tip.id, position: Number(modalData.position ?? 1) })
			}}
			onCancel={() => onCancel && onCancel()}
			width={600}
			okText={t("confirm")}
			cancelText={t("cancel")}
		>
			<MainForm
				initialValues={modalData}
				hideFormButtons
				key={"micrositeTipsReviewModal"}
				onChangeFormValues={formData => {
					if (JSON.stringify(modalData) !== JSON.stringify(formData)) {
						setModalData({
							...modalData,
							...formData
						})
					}
				}}
				className="flex flex-col justify-center items-center"
				ref={formRef}
			>
				<Row gutter={[24, 0]} className="w-full">
					<Col md={24} xl={24} xs={24}>
						<Row gutter={[24, 0]}>
							<Col xl={22} md={22} xs={22} className="mx-auto">
								<FormField.SelectSearch
									key={"id"}
									label={t("tip")}
									name={"id"}
									mandatory
									value={modalData?.id}
									placeholder={t("tipPlaceholder").toString()}
									asyncFetchSearchFunction={search =>
										search ||
										micrositeViewModel.searchedTips.length < micrositeViewModel.tips.length
											? micrositeViewModel.searchTips(search)
											: Promise.resolve()
									}
									onChange={selected => {
										setModalData({
											...modalData,
											id: selected.value
										})
									}}
									options={micrositeViewModel.searchedTips
										.filter(tip => !micrositeViewModel.formData.tips?.some(t => tip.id === t.tipId))
										.map(tip => ({
											label: `${tip.id} - ${tip.user.name} - ${tip.description}`,
											value: tip.id,
											title: tip.description
										}))}
									popupMatchSelectWidth={true}
								/>
							</Col>
						</Row>
						<Row gutter={[24, 0]}>
							<Col xl={22} md={22} xs={22} className="mx-auto mb-2">
								<FormField.Input
									inputType={InputType.NUMBER}
									key={"position"}
									label={t("position")}
									name={"position"}
									mandatory
								/>
							</Col>
						</Row>
					</Col>
				</Row>
			</MainForm>
		</Modal>
	)
}

export default observer(MicrositeTipModal)
